import React, { useState, useEffect, useMemo } from 'react';
import { 
  XMarkIcon, 
  CalendarIcon, 
  UserIcon, 
  CubeIcon, 
  TruckIcon,
  ShieldExclamationIcon,
  DocumentTextIcon,
  TagIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { Combobox } from '@headlessui/react';
import { CreateWarrantyData, WarrantyType, Warranty } from '../../types/warranty';
import { useProducts } from '../../hooks/useProducts';
import { useCustomers } from '../../hooks/useCustomers';
import { useSuppliers } from '../../hooks/useSuppliers';
import Modal from '../common/Modal';

interface WarrantyFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateWarrantyData) => Promise<void>;
  warranty?: Warranty;
  loading?: boolean;
}

const WarrantyForm: React.FC<WarrantyFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  warranty,
  loading = false
}) => {
  const { products } = useProducts();
  const { customers } = useCustomers();
  const { suppliers } = useSuppliers();

  // Search states
  const [productQuery, setProductQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');

  const [formData, setFormData] = useState<CreateWarrantyData>({
    productId: '',
    customerId: '',
    type: 'manufacturer' as WarrantyType,
    startDate: new Date(),
    durationMonths: 12,
    purchaseDate: new Date(),
    invoiceNumber: '',
    purchasePrice: 0,
    currency: 'HUF',
    supplierId: '',
    serialNumber: '',
    batchNumber: '',
    manufacturer: '',
    model: '',
    terms: '',
    coverage: [],
    exclusions: [],
    notes: ''
  });



  // Reset search queries when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setProductQuery('');
      setCustomerQuery('');
    }
  }, [isOpen]);

  // Initialize form with warranty data if editing
  useEffect(() => {
    if (warranty) {
      setFormData({
        productId: warranty.productId,
        customerId: warranty.customerId,
        type: warranty.type,
        startDate: warranty.startDate,
        durationMonths: warranty.durationMonths,
        purchaseDate: warranty.purchaseDate,
        invoiceNumber: warranty.invoiceNumber || '',
        purchasePrice: warranty.purchasePrice || 0,
        currency: warranty.currency,
        supplierId: warranty.supplierId || '',
        serialNumber: warranty.serialNumber || '',
        batchNumber: warranty.batchNumber || '',
        manufacturer: warranty.manufacturer || '',
        model: warranty.model || '',
        terms: warranty.terms,
        coverage: warranty.coverage,
        exclusions: warranty.exclusions,
        notes: warranty.notes || ''
      });
    } else {
      // Reset form for new warranty
      setFormData({
        productId: '',
        customerId: '',
        type: 'manufacturer',
        startDate: new Date(),
        durationMonths: 12,
        purchaseDate: new Date(),
        invoiceNumber: '',
        purchasePrice: 0,
        currency: 'HUF',
        supplierId: '',
        serialNumber: '',
        batchNumber: '',
        manufacturer: '',
        model: '',
        terms: '',
        coverage: [],
        exclusions: [],
        notes: ''
      });
    }
  }, [warranty, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.productId) {
      alert('Kérjük válasszon terméket!');
      return;
    }
    
    if (!formData.customerId) {
      alert('Kérjük válasszon ügyfelet!');
      return;
    }
    
    if (!formData.terms.trim()) {
      alert('Kérjük adja meg a garancia feltételeit!');
      return;
    }
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting warranty form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleDateChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: new Date(value)
    }));
  };



  // Filtered lists for search
  const filteredProducts = useMemo(() => {
    if (!productQuery) return products.slice(0, 20); // Show first 20 if no search
    return products.filter(product => {
      const searchLower = productQuery.toLowerCase();
      return (
        product.name.toLowerCase().includes(searchLower) ||
        product.code?.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower)
      );
    }).slice(0, 20);
  }, [products, productQuery]);

  const filteredCustomers = useMemo(() => {
    if (!customerQuery) return customers.slice(0, 20); // Show first 20 if no search
    return customers.filter(customer => {
      const searchLower = customerQuery.toLowerCase();
      const customerName = customer.type === 'organization' 
        ? customer.companyName 
        : `${customer.firstName} ${customer.lastName}`;
      
      return (
        customerName?.toLowerCase().includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower) ||
        customer.taxNumber?.toLowerCase().includes(searchLower)
      );
    }).slice(0, 20);
  }, [customers, customerQuery]);

  const selectedProduct = products.find(p => p.id === formData.productId);
  const selectedCustomer = customers.find(c => c.id === formData.customerId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={warranty ? 'Garancia szerkesztése' : 'Új garancia'}
      size="4xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Information */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-blue-500 to-indigo-600">
              <ShieldExclamationIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Alapadatok</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Product Selection */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <CubeIcon className="w-4 h-4 inline mr-1" />
                  Termék *
                </label>
                <Combobox
                  value={formData.productId}
                  onChange={(productId) => setFormData(prev => ({ ...prev, productId }))}
                >
                  <div className="relative">
                    <div className="relative w-full">
                      <Combobox.Input
                        className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                        onChange={event => setProductQuery(event.target.value)}
                        onClick={() => setProductQuery('')}
                        displayValue={(productId: string) => {
                          const product = products.find(p => p.id === productId);
                          return product ? `${product.name} (${product.code})` : '';
                        }}
                        placeholder="Termék keresése..."
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                    <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {filteredProducts.length === 0 && productQuery !== '' ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-500 dark:text-gray-400">
                          Nincs találat
                        </div>
                      ) : (
                        filteredProducts.map(product => (
                          <Combobox.Option
                            key={product.id}
                            value={product.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-900 dark:text-primary-100' : 'text-gray-800 dark:text-gray-200'}`
                            }
                          >
                            <div>
                              <div className="font-medium">{product.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">Kód: {product.code}</div>
                            </div>
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
              </div>

              {/* Customer Selection */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  Ügyfél *
                </label>
                <Combobox
                  value={formData.customerId}
                  onChange={(customerId) => setFormData(prev => ({ ...prev, customerId }))}
                >
                  <div className="relative">
                    <div className="relative w-full">
                      <Combobox.Input
                        className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                        onChange={event => setCustomerQuery(event.target.value)}
                        onClick={() => setCustomerQuery('')}
                        displayValue={(customerId: string) => {
                          const customer = customers.find(c => c.id === customerId);
                          if (!customer) return '';
                          return customer.type === 'organization'
                            ? customer.companyName || ''
                            : `${customer.firstName} ${customer.lastName}`;
                        }}
                        placeholder="Ügyfél keresése..."
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                    <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {filteredCustomers.length === 0 && customerQuery !== '' ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-500 dark:text-gray-400">
                          Nincs találat
                        </div>
                      ) : (
                        filteredCustomers.map(customer => (
                          <Combobox.Option
                            key={customer.id}
                            value={customer.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-900 dark:text-primary-100' : 'text-gray-800 dark:text-gray-200'}`
                            }
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`p-1 rounded ${customer.type === 'organization' ? 'bg-blue-100 dark:bg-blue-900/20' : 'bg-purple-100 dark:bg-purple-900/20'}`}>
                                {customer.type === 'organization' ? (
                                  <BuildingOffice2Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                ) : (
                                  <UserIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium">
                                  {customer.type === 'organization'
                                    ? customer.companyName
                                    : `${customer.firstName} ${customer.lastName}`}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{customer.email}</div>
                              </div>
                            </div>
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
              </div>
            </div>
          </div>
        </div>

        {/* Warranty Details */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-emerald-500 to-teal-600">
              <TagIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Garancia részletek</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Warranty Type */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Garancia típusa *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                >
                  <option value="manufacturer">Gyártói garancia</option>
                  <option value="seller">Forgalmazói garancia</option>
                  <option value="extended">Kiterjesztett garancia</option>
                  <option value="service">Szerviz garancia</option>
                </select>
              </div>

              {/* Duration */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Időtartam (hónap) *
                </label>
                <input
                  type="number"
                  name="durationMonths"
                  value={formData.durationMonths}
                  onChange={handleInputChange}
                  min="1"
                  max="120"
                  required
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-purple-500 to-pink-600">
              <CalendarIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Dátumok</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Start Date */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  Garancia kezdete *
                </label>
                <input
                  type="date"
                  value={formData.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  required
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>

              {/* Purchase Date */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  Vásárlás dátuma *
                </label>
                <input
                  type="date"
                  value={formData.purchaseDate.toISOString().split('T')[0]}
                  onChange={(e) => handleDateChange('purchaseDate', e.target.value)}
                  required
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Information */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-orange-500 to-yellow-600">
              <DocumentTextIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Vásárlási információk</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Invoice Number */}
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Számla szám
                </label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>

              {/* Purchase Price */}
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vásárlási ár
                </label>
                <input
                  type="number"
                  name="purchasePrice"
                  value={formData.purchasePrice}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>

              {/* Currency */}
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Valuta
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                >
                  <option value="HUF">HUF</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-green-500 to-teal-600">
              <CubeIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">További részletek</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Serial Number */}
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sorozatszám
                </label>
                <input
                  type="text"
                  name="serialNumber"
                  value={formData.serialNumber}
                  onChange={handleInputChange}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>

              {/* Manufacturer */}
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gyártó
                </label>
                <input
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleInputChange}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>

              {/* Model */}
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Modell
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Coverage and Notes */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-indigo-500 to-purple-600">
              <DocumentTextIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Lefedettség és megjegyzések</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Terms */}
              <div className="col-span-12">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Garancia feltételek *
                </label>
                <textarea
                  name="terms"
                  value={formData.terms}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                  placeholder="Írja le a garancia feltételeit..."
                />
              </div>

              {/* Coverage */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mit fedez a garancia
                </label>
                <textarea
                  name="coverage"
                  value={Array.isArray(formData.coverage) ? formData.coverage.join('\n') : formData.coverage}
                  onChange={(e) => setFormData(prev => ({ ...prev, coverage: e.target.value.split('\n') }))}
                  rows={4}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                  placeholder="Soronként egy fedezeti elemet írjon..."
                />
              </div>

              {/* Exclusions */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kizárások
                </label>
                <textarea
                  name="exclusions"
                  value={Array.isArray(formData.exclusions) ? formData.exclusions.join('\n') : formData.exclusions}
                  onChange={(e) => setFormData(prev => ({ ...prev, exclusions: e.target.value.split('\n') }))}
                  rows={4}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                  placeholder="Soronként egy kizárást írjon..."
                />
              </div>

              {/* Notes */}
              <div className="col-span-12">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Megjegyzések
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                  placeholder="További megjegyzések..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg text-sm hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {loading ? 'Mentés...' : (warranty ? 'Frissítés' : 'Létrehozás')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default WarrantyForm; 